import React, { FunctionComponent, useEffect, Fragment, useContext, useRef } from 'react';
import { ArrowDown, ArrowLongDown, Check, Stripe } from '../../../icons/components';
import classNames from '../../../utils/classnames';
import { useStepper } from '../../../utils/hooks';
//import useMediaQuery from '../../../utils/hooks/use-media';
import { Button } from '../Button';
import { FormContext } from '../../../store/surplus/SurPlusContext';
import { mainMediaMobile } from '../../../utils';
import useMediaQuery from '../../../utils/hooks/use-media';

interface StepperStep {
  id: number;
}

export const StepperStep: FunctionComponent<StepperStep> = ({ children, id }) => {
  const { steps } = useStepper();
  const { isActive, isTouched, isValid } = steps[id];

  const classes = classNames('stepper__step', {
    'stepper__step--is-active': isActive,
    'stepper__step--is-valid': isValid,
    'stepper__step--is-touched': isTouched,
  });

  return <div className={classes}>{children}</div>;
};

export const StepperContent: FunctionComponent = ({ children }) => (
  <div className="stepper__step-content">{children}</div>
);

export const StepperHeader: FunctionComponent<{
  id: number;
  title: string;
  completedText?: string;
}> = ({ id, title, completedText }) => {
  const { steps } = useStepper();
  const { isActive, isTouched, isValid } = steps[id];
  const { setStepActive } = useStepper();
  const mediaMatches = useMediaQuery(mainMediaMobile);

  const handleClick = () => {
    if (!mediaMatches) {
      requestAnimationFrame(() => {
        setStepActive(id, !isActive);
      });
    }
  };

  return (
    <div
      className="stepper__step-header  u-flex u-flex-middle u-flex-between"
      onClick={handleClick}
    >
      <div className="u-flex u-flex-middle">
                <span className="stepper__step-checkmark">
                    <Check height={12} width={16} />
                </span>
        <span className="stepper__step-title">{title}</span>
      </div>

      <div className="u-flex u-flex-middle">
        {completedText && (
          <span className="stepper__step-completed-title">{completedText}</span>
        )}

        {(isActive || isTouched || isValid) && (
          <button
            className={`stepper__step-button ${!isActive ? 'stepper__step-button--open' : ''}`}
            title={!isActive ? 'Openen' : 'Sluiten'}
          >
            {!isActive && <ArrowDown width={13} height={8} />}
            {isActive && <Stripe width={12} height={3} />}
          </button>
        )}
      </div>
    </div>
  );
};

export const StepperSubmit: FunctionComponent<{
  isValidated?: boolean;
  id: number;
  variant?: 'primary';
  isLastStep?: boolean;
  isLoading?: boolean;
  onStepChange?: () => void;
}> = ({ isValidated, id, variant, isLastStep, isLoading, onStepChange }) => {
  const { setCurrentStep, setStepActive, setStepValid } = useStepper();

  const handleNextStep = () => {
    requestAnimationFrame(() => {
      setStepValid(id, true);
      if (!isLastStep) setCurrentStep(id + 1);
      setStepActive(id, false);
      if (onStepChange) onStepChange();
    });
  };

  return (
    <Fragment>
      {variant === 'primary' && (
        <div className="stepper__step-button-holder stepper__step-button-holder--centered">
          <Button
            label="Woningwaarde berekenen"
            variant="tertiary"
            disabled={!isValidated}
            onClick={handleNextStep}
            isLoading={isLoading}
          />
        </div>
      )}
      {!variant && (
        <div className="stepper__step-button-holder">
          <span className="stepper__step-next">Ga verder</span>
          <button
            type="submit"
            className="stepper__step-next stepper__step-next-icon"
            disabled={!isValidated}
            onClick={handleNextStep}
          >
            <ArrowLongDown width={10} height={12} />
          </button>
        </div>
      )}
    </Fragment>
  );
};

export const Stepper: FunctionComponent<{
  defaultUrl: string;
  previousButtonTexts: string[];
  defaultPreviousText: string;
  activeStep?: number;
}> = ({ children, defaultUrl, defaultPreviousText, previousButtonTexts, activeStep }) => {
  const { setSteps, steps, currentStep, setCurrentStep, setStepActive } = useStepper();
  const formContext = useContext(FormContext);
  const hasCurrentStep = currentStep && currentStep !== 0;

  const handleBackClick = () => {
    requestAnimationFrame(() => {
      if (currentStep && currentStep !== 0) {
        const id = currentStep - 1;
        setStepActive(currentStep, false);
        setCurrentStep(id);
        formContext.updateData(null);
      } else {
        window.location.href = defaultUrl;
      }
    });
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      const stepperSteps = React.Children.toArray(children).map((step: any) => {
        return {
          ...step.props,
          isValid: false,
          isActive: activeStep ? activeStep === step.props.id : currentStep === step.props.id,
          isTouched: activeStep ? activeStep === step.props.id : currentStep === step.props.id,
        };
      });
      setSteps(stepperSteps);
    });
  }, []); // eslint-disable-line

  return (
    <div className="c-stepper">
      <div className="stepper__indicator">
        {steps.length > 0 &&
          steps.map((step, key) => (
            <span
              key={key}
              className={`stepper__indicator-item ${step.isTouched ? 'stepper__indicator-item--is-valid' : ''}`}
            ></span>
          ))}
      </div>
      <div className="stepper__wrapper">
        {hasCurrentStep && currentStep ? (
          <button className="stepper__back-button" onClick={handleBackClick}>
                        <span className="stepper__back-button-icon">
                            <ArrowLongDown width={12} />
                        </span>
            {previousButtonTexts[currentStep - 1]}
          </button>
        ) : null}
        {steps.length > 0 && children}
      </div>
    </div>
  );
};
